import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormNavigationProvider, NavTextField, NavSelect } from '../components/FormNavigation';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchCalculatedValueDefinitions,
  addCalculatedValueDefinition,
  updateCalculatedValueDefinition,
  removeCalculatedValueDefinition,
} from '../store/applicationSettingsSlice';
import { apiService } from '../services/api';
import { showNotification } from '../store/notificationSlice';
import type { CalculatedValueDefinition } from '../types/applicationSettings';
import { parseDSL } from '../utils/calculatedValueEvaluator';

// ── Dialog ────────────────────────────────────────────────────────────────────

interface FieldOption { name: string; title: string }

interface DialogState {
  label: string;
  triggerFieldName: string;
  code: string;
  codeError: string;
}

const EMPTY_STATE: DialogState = { label: '', triggerFieldName: '', code: '', codeError: '' };

const SYNTAX_HINT =
  'Keywords: if · else if · else · end if  |  ' +
  '{field} current · P{field} prev entry · D{field} prev day · M{field} prev month · Y{field} prev year  |  ' +
  '+ - * /  ·  ROUND(x)  CEIL(x)  FLOOR(x)  ABS(x)  MOD(a,b)  CONCAT(a,b)  |  ' +
  'Result: !Error on calc error · N/A when no branch matches';

interface DefinitionDialogProps {
  open: boolean;
  initial: CalculatedValueDefinition | null;
  fieldOptions: FieldOption[];
  saving: boolean;
  onSave: (def: Omit<CalculatedValueDefinition, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onClose: () => void;
}

function DefinitionDialog({ open, initial, fieldOptions, saving, onSave, onClose }: DefinitionDialogProps) {
  const [state, setState] = useState<DialogState>(EMPTY_STATE);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setState(
        initial
          ? { label: initial.label, triggerFieldName: initial.triggerFieldName, code: initial.code, codeError: '' }
          : EMPTY_STATE,
      );
    }
  }, [open, initial]);

  const set = (field: keyof DialogState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setState((prev) => ({ ...prev, [field]: e.target.value, codeError: field === 'code' ? '' : prev.codeError }));

  const handleTriggerChange = (e: any) =>
    setState((prev) => ({ ...prev, triggerFieldName: e.target.value as string }));

  const handleSave = () => {
    const code = state.code.trim();
    if (code) {
      try {
        parseDSL(code);
      } catch (err) {
        setState((prev) => ({ ...prev, codeError: (err as Error).message }));
        return;
      }
    }
    onSave({ label: state.label.trim(), triggerFieldName: state.triggerFieldName, code });
  };

  const isValid = state.label.trim() !== '' && state.triggerFieldName !== '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>{initial ? 'Edit Calculated Value' : 'New Calculated Value'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '20px !important' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <NavTextField
              label="Label (key) *"
              value={state.label}
              onChange={set('label')}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <FormControl size="small" sx={{ minWidth: 260 }} required>
              <InputLabel shrink>Trigger Field</InputLabel>
              <NavSelect
                value={state.triggerFieldName}
                onChange={handleTriggerChange}
                label="Trigger Field *"
                displayEmpty
                renderValue={(v: any) => v
                  ? (fieldOptions.find((f) => f.name === v)?.title ?? v)
                  : <em style={{ color: '#aaa' }}>— pick field —</em>
                }
              >
                {fieldOptions.map((f) => (
                  <MenuItem key={f.name} value={f.name}>{f.title} <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>({f.name})</Typography></MenuItem>
                ))}
              </NavSelect>
            </FormControl>
          </Box>

          <Box>
            <NavTextField
              label="Code"
              value={state.code}
              onChange={set('code')}
              multiline
              rows={13}
              fullWidth
              error={!!state.codeError}
              placeholder={`if {grossSalary} - M{grossSalary} > 0\n  {grossSalary} - M{grossSalary}\nelse if {grossSalary} - M{grossSalary} = 0\n  "No change"\nelse\n  0\nend if`}
              slotProps={{
                inputLabel: { shrink: true },
                input: { style: { fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.6 } },
              }}
            />
            {state.codeError
              ? <Alert severity="error" sx={{ mt: 0.75, py: 0.25 }}>{state.codeError}</Alert>
              : <FormHelperText sx={{ mx: 0.25, mt: 0.5, fontSize: '0.72rem' }}>{SYNTAX_HINT}</FormHelperText>
            }
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!isValid || saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CalculatedValuesSettings() {
  const dispatch = useAppDispatch();
  const definitions = useAppSelector((s) => s.applicationSettings.calculatedValueDefinitions);
  const uiFields = useAppSelector((s) => s.applicationSettings.uiFields);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CalculatedValueDefinition | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchCalculatedValueDefinitions()).finally(() => setIsLoading(false));
  }, [dispatch]);

  const fieldOptions: FieldOption[] = uiFields.map((f) => ({ name: f.name, title: f.title }));
  const uiFieldMap = new Map(uiFields.map((f) => [f.name, f]));

  const handleOpenNew = useCallback(() => { setEditing(null); setDialogOpen(true); }, []);
  const handleOpenEdit = useCallback((def: CalculatedValueDefinition) => { setEditing(def); setDialogOpen(true); }, []);

  const handleSave = useCallback(async (payload: Omit<CalculatedValueDefinition, 'id' | 'createdAt' | 'updatedAt'>) => {
    setSaving(true);
    try {
      if (editing) {
        const res = await apiService.updateCalculatedValueDefinition(editing.id, payload);
        dispatch(updateCalculatedValueDefinition(res.data));
        dispatch(showNotification({ message: 'Calculated value updated.', severity: 'success' }));
      } else {
        const res = await apiService.createCalculatedValueDefinition(payload);
        dispatch(addCalculatedValueDefinition(res.data));
        dispatch(showNotification({ message: 'Calculated value created.', severity: 'success' }));
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save.';
      dispatch(showNotification({ message, severity: 'error' }));
    } finally {
      setSaving(false);
    }
  }, [dispatch, editing]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await apiService.deleteCalculatedValueDefinition(id);
      dispatch(removeCalculatedValueDefinition(id));
      dispatch(showNotification({ message: 'Deleted.', severity: 'success' }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete.';
      dispatch(showNotification({ message, severity: 'error' }));
    } finally {
      setDeletingId(null);
    }
  }, [dispatch]);

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', m: -3, minHeight: 'calc(100vh - 64px)' }}>
        <Box sx={{ flexGrow: 1, p: 3, maxWidth: 900 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>Calculated Values</Typography>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => dispatch(fetchCalculatedValueDefinitions())} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNew}>
              + New
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Each entry displays a calculated result in the employee entry sidebar when the user focuses its trigger field.
            Write an if/else if/else/end if script — or just a formula — in the code editor.
          </Typography>

          {isLoading && <CircularProgress size={24} />}

          {!isLoading && definitions.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No calculated values defined yet.</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenNew} sx={{ mt: 1.5 }}>
                Create first
              </Button>
            </Paper>
          )}

          {definitions.map((def) => {
            const triggerField = uiFieldMap.get(def.triggerFieldName);
            const codePreview = def.code.split('\n').find((l) => l.trim())?.trim() ?? '';
            return (
              <Paper key={def.id} variant="outlined" sx={{ mb: 1.5, p: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600}>{def.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Trigger: {triggerField?.title ?? def.triggerFieldName}
                  </Typography>
                  {codePreview && (
                    <Typography variant="caption" color="text.disabled" fontFamily="monospace" noWrap display="block">
                      {codePreview}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenEdit(def)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(def.id)}
                      disabled={deletingId === def.id}
                    >
                      {deletingId === def.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>

      <DefinitionDialog
        open={dialogOpen}
        initial={editing}
        fieldOptions={fieldOptions}
        saving={saving}
        onSave={handleSave}
        onClose={() => setDialogOpen(false)}
      />
    </DashboardLayout>
  );
}
